/**
 * Gift with Purchase Service
 *
 * Purpose:
 * - Provides a service to add gift (measuring tape) for first-time customers
 * - Designed to be called explicitly before opening cart drawer
 * - Usage: await window.giftService.addGifts()
 *
 * Integration points:
 * - Product forms: Call before opening cart drawer
 * - Recommendation modal: Call before opening cart drawer
 */
(function () {
  try {
    const DEBUG_PREFIX = '[giftService]';
    const LS_KEY = 'snapfit.gwp.attempted';
    const FALLBACK_VARIANT = 50615496376635;

    function log(...args) {
      try { console.log(DEBUG_PREFIX, ...args); } catch (_) {}
    }
    function warn(...args) {
      try { console.warn(DEBUG_PREFIX, ...args); } catch (_) {}
    }

    log('initializing gift service');

    // Read config from window.ShopGiftConfig (injected by theme.liquid)
    const cfg = window.ShopGiftConfig || {};
    const enabled = cfg.enabled === true;
    const giftVariantId = Number(cfg.giftVariantId) || FALLBACK_VARIANT;
    const discountCode = cfg.discountCode || 'WELCOME';
    const customerHasOrders = cfg.customerHasOrders === true;

    log('config:', { enabled, giftVariantId, discountCode, customerHasOrders });

    // Check if gift was already attempted
    function hasAttempted() {
      try {
        return localStorage.getItem(LS_KEY) === '1';
      } catch (e) {
        return false;
      }
    }

    // Mark gift as attempted
    function markAttempted() {
      try {
        localStorage.setItem(LS_KEY, '1');
        log('marked as attempted');
      } catch (e) {
        warn('failed to mark attempted');
      }
    }

    // Check if cart has gift
    async function cartHasGift() {
      try {
        const r = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!r.ok) return false;
        const cart = await r.json();
        return cart.items?.some(item => Number(item.variant_id) === Number(giftVariantId));
      } catch (e) {
        return false;
      }
    }

    // Apply discount code via Shopify Cart API
    async function applyDiscount() {
      if (!discountCode) return false;
      
      try {
        log('applying discount via cart API:', discountCode);
        const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ discount: discountCode })
        });
        
        if (response.ok) {
          log('discount applied successfully');
          return true;
        } else {
          warn('discount application failed:', response.status);
          return false;
        }
      } catch (e) {
        warn('discount error:', e.message);
        return false;
      }
    }

    // Main service method - add gifts if eligible
    async function addGifts() {
      try {
        log('addGifts called');

        // Check eligibility
        if (!enabled) {
          log('feature disabled');
          return false;
        }

        if (customerHasOrders) {
          log('customer has orders - not eligible');
          return false;
        }

        if (hasAttempted()) {
          log('already attempted');
          return false;
        }

        if (await cartHasGift()) {
          log('gift already in cart');
          markAttempted();
          return false;
        }

        // Add gift
        log('adding gift:', giftVariantId);
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: giftVariantId, quantity: 1 })
        });

        if (!r.ok) {
          const err = await r.text().catch(() => '');
          warn('add failed:', r.status, err);
          return false;
        }

        const result = await r.json();
        log('gift added:', result);
        markAttempted();

        // Apply discount code immediately
        await applyDiscount();
        
        // Re-apply cart grouping after adding gift + discount
        if (window.CartGrouping) {
          // Small delay to let cart API finish
          setTimeout(() => {
            try {
              // Reset timing protection
              window.CartGrouping.lastUnifiedUpdate = 0;
              // Trigger regrouping
              new window.CartGrouping();
              log('cart grouping re-applied');
            } catch (e) {
              warn('cart grouping failed:', e.message);
            }
          }, 500);
        }

        return true;
      } catch (e) {
        warn('error:', e.message);
        return false;
      }
    }

    // Create and expose service
    window.giftService = {
      addGifts,
      hasAttempted,
      cartHasGift,
      reset: () => {
        try {
          localStorage.removeItem(LS_KEY);
          log('reset completed');
        } catch (e) {
          warn('reset failed');
        }
      }
    };

    log('gift service ready - use window.giftService.addGifts()');
  } catch (e) {
    try { console.error('[giftService] init error:', e.message); } catch (_) {}
  }
})();